import React, { useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { nullSafeGet, nullSafeGetOrElse, nullSafeGetWithZero } from '../../utils/DataAccessUtils';
import { ENTITY_REQUEST_ANSWER_TYPES } from '../../types/entity_add_requests';
import useMountEffect from '../../hooks/useMountEffect';
import { withUserType } from '../withUserType';
import { locationsRestCrudThunks } from '../../thunks/locations_thunks';
import OWTag from '../common/OWTag';
import ProblemTypeHierarchicalDisplay from '../problem_type_hierarchical_display/ProblemTypeHierarchicalDisplay';

const LOCATIONS_TC = 'ENTITY_REQUEST_SUPPLIER_LOCATIONS_AUTOCOMPLETE';

const displayAnswer = (answerEntry) => {
	const answerType = nullSafeGet('question.answerType', answerEntry);
	// nullSafeGet returns undefined for falsy values, which would hide a legitimate "No" (false) or
	// 0 answer as "—"; nullSafeGetWithZero preserves false/0 so negative/zero answers stay visible.
	const answer = nullSafeGetWithZero('answer', answerEntry);
	if (answer === undefined || answer === null || answer === '') {
		return '—';
	}
	if (answerType === ENTITY_REQUEST_ANSWER_TYPES.yes_no) {
		return answer ? 'Yes' : 'No';
	}
	return String(answer);
};

const SectionHeader = ({ children }) => (
	<div className="bodybold mb-3 uppercase text-character-primary">{children}</div>
);

const DetailField = ({ label, children }) => (
	<div className="mb-6">
		<div className="bodyregular text-character-secondary">{label}</div>
		<div className="bodyregular mt-1 text-character-primary">{children}</div>
	</div>
);

// Read-only summary of an entity add request: proposed supplier info + the answered request form.
// Resolves problem-type / location ids to names for display.
function EntityAddRequestDetails({ record, history, userType, fetchMultipleLocations }) {
	const payload = nullSafeGetOrElse('payload', record, {});
	const answers = nullSafeGetOrElse('answers', record, []);
	const allProblemTypes = nullSafeGet('allProblemTypes', payload);
	const allLocations = nullSafeGet('allLocations', payload);
	const problemTypeIds = nullSafeGetOrElse('problemTypeIds', payload, []);
	const locationIds = nullSafeGetOrElse('locationIds', payload, []);
	const createdEntityId = nullSafeGet('createdEntityId', record);

	const [locationNames, setLocationNames] = useState([]);

	useMountEffect(() => {
		if (!allLocations && locationIds.length > 0) {
			fetchMultipleLocations(locationIds)
				.then((records) => setLocationNames((records || []).map((record) => record.name)))
				.catch(() => {});
		}
	});

	// Coverage reads the same way for both dimensions: "everything" is plain text, and a narrower
	// selection is a tag per item — the shape ProblemTypeHierarchicalDisplay's pill mode produces.
	const locationsDisplay = allLocations ? (
		'All Locations'
	) : locationNames.length > 0 ? (
		<div className="inline-flex flex-wrap gap-1">
			{locationNames.map((locationName) => (
				<OWTag key={locationName}>{locationName}</OWTag>
			))}
		</div>
	) : (
		`${locationIds.length} selected`
	);

	const viewCreatedSupplier = () => {
		if (createdEntityId) {
			history.push(`/${userType}/suppliers/detail/${createdEntityId}`);
		}
	};

	return (
		<div>
			<SectionHeader>New Supplier Info</SectionHeader>
			<DetailField label="Supplier Company Name">
				{createdEntityId ? (
					<span
						className="text-primary cursor-pointer underline"
						style={{ color: '#6027b0' }}
						onClick={viewCreatedSupplier}
					>
						{nullSafeGetOrElse('companyName', payload, '—')}
					</span>
				) : (
					nullSafeGetOrElse('companyName', payload, '—')
				)}
			</DetailField>
			<DetailField label="Supplier Contact email address">
				{nullSafeGetOrElse('contactEmail', payload, '—')}
			</DetailField>
			<DetailField label="Contact first name">
				{nullSafeGetOrElse('contactFirstName', payload, '—')}
			</DetailField>
			<DetailField label="Contact last name">
				{nullSafeGetOrElse('contactLastName', payload, '—')}
			</DetailField>
			<DetailField label="Problem Types">
				{allProblemTypes ? (
					// Not delegated to ProblemTypeHierarchicalDisplay: it tags its own "All Problem Types"
					// empty state, which would read as a selected problem type next to plain "All Locations".
					'All Problem Types'
				) : (
					<ProblemTypeHierarchicalDisplay problemTypeIds={problemTypeIds} displayMode="pill" />
				)}
			</DetailField>
			<DetailField label="Locations">{locationsDisplay}</DetailField>

			{answers.length > 0 ? (
				<>
					<div className="my-4" style={{ borderTop: '1px solid #f0f0f0' }} />
					<SectionHeader>Request Form</SectionHeader>
					{answers.map((answerEntry) => (
						<DetailField
							key={nullSafeGet('orderingId', answerEntry)}
							label={nullSafeGet('question.questionText', answerEntry)}
						>
							{displayAnswer(answerEntry)}
						</DetailField>
					))}
				</>
			) : null}
		</div>
	);
}

const mapStateToProps = (state) => ({
	locations: state.locations,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	fetchMultipleLocations: (ids) =>
		dispatch(locationsRestCrudThunks(ownProps.userType).readMultiple(ids, LOCATIONS_TC)),
});

export default withUserType(
	withRouter(connect(mapStateToProps, mapDispatchToProps)(EntityAddRequestDetails))
);
